import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { WPRangedInput } from "../../../packages/WPInput";

import {
  FIELD_RELATIONSHIPS,
  REPLACEMENT_THRESHOLD,
} from "./replacementThreshold.config";

import { COMPARE_KEYS } from "../../../constants/global.constants";

import IconWithTooltip from "../../../common/styles/components/headings/IconWithTooltip";
import { LICENSE_PERMISSION_IDS } from "../../../constants/LicenseManagement.constants";
import { hasEligibleLicensePermission } from "../../../utils/LicenseUtils";

import { getadvanceSettings } from "../../../network/cipAPI";
import {
  storeAdvancedSettings,
  storeReplacementThreshold,
} from "../../../Store/TemplateStore/template.action";

import "./index.scss";

const isPermeabilityTriggerEnabled = (value) =>
  value === true || Number(value) === 1;

const ReplacementThreshold = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
    nwp_trigger,
    flow,
    pressure,
    permeate_quality,
    selTrain: selectedTrain,
  } = useSelector((state) => state);
  const usePermeabilityTriggerRaw = useSelector(
    (state) => state.advancedSettings?.use_permeability_trigger,
  );

  const usePermeabilityTrigger = isPermeabilityTriggerEnabled(
    usePermeabilityTriggerRaw,
  );
  const userLicenses = useSelector((state) => state.userLicenses || []);
  const hasEditableUsePermeabilityLicense = hasEligibleLicensePermission(
    userLicenses,
    LICENSE_PERMISSION_IDS.USE_PERMEABILITY,
  );
  const shouldDisablePermeabilityThreshold =
    !usePermeabilityTrigger || !hasEditableUsePermeabilityLicense;

  const thresholdFieldsConfig = useMemo(
    () => REPLACEMENT_THRESHOLD({ pressure, permeate_quality, flow }),
    [pressure, permeate_quality, flow],
  );

  const [thresholdState, setThresholdState] = useState({
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
    nwp_trigger,
  });

  useEffect(() => {
    setThresholdState({
      max_feed_pressure,
      max_permeate_quality,
      std_warranty_period,
      startup_pressure_drop,
      designed_permeate_flow,
      std_membrane_age,
      max_pressure_drop,
      min_permeate_flow,
      nwp_trigger,
    });
  }, [
    max_feed_pressure,
    max_permeate_quality,
    std_warranty_period,
    startup_pressure_drop,
    designed_permeate_flow,
    std_membrane_age,
    max_pressure_drop,
    min_permeate_flow,
    nwp_trigger,
  ]);

  const [errors, setErrors] = useState({
    max_feed_pressure: null,
    max_permeate_quality: null,
    std_warranty_period: null,
    startup_pressure_drop: null,
    designed_permeate_flow: null,
    std_membrane_age: null,
    max_pressure_drop: null,
    min_permeate_flow: null,
    nwp_trigger: null,
  });

  const [noError, setNoError] = useState(true);

  useEffect(() => {
    const loadUsePermeabilityTrigger = async () => {
      try {
        const response = await getadvanceSettings(selectedTrain);
        const usePermeabilityTrigger =
          response?.data?.settings?.use_permeability_trigger;

        if (usePermeabilityTrigger === undefined) {
          return;
        }

        dispatch(
          storeAdvancedSettings({
            use_permeability_trigger: isPermeabilityTriggerEnabled(
              usePermeabilityTrigger,
            ),
          }),
        );
      } catch (error) {
        throw error;
      }
    };

    if (selectedTrain) {
      loadUsePermeabilityTrigger();
    }
  }, [dispatch, selectedTrain]);

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null,
    );
    setNoError(noError);
    props.setNoThresholdError(noError);
  }, [errors]);

  useEffect(() => {
    let allMandatoryFieldsFilled = true;
    Object.keys(thresholdFieldsConfig).forEach((fieldId) => {
      const fieldConfig = thresholdFieldsConfig[fieldId];
      const { isRequired } = fieldConfig;
      if (isRequired) {
        const hasValue = thresholdState[fieldId];
        if (hasValue === null || hasValue === undefined || hasValue === "") {
          setErrors({
            ...errors,
            [fieldId]: true,
          });
          allMandatoryFieldsFilled = false;
        }
      }
    });
    props.setMandatoryThresholdFilled(allMandatoryFieldsFilled);
  }, [thresholdState, props.isNextClicked]);

  const formatRelatedFields = (field, value) => {
    const relationship = FIELD_RELATIONSHIPS[field];

    if (!relationship) return null;

    const { targetField, conversion } = relationship;
    const calculatedValue = value === "" ? "" : conversion(value);

    return { [targetField]: calculatedValue };
  };

  const validateCrossFieldRules = (fieldName, numValue) => {
    // Check if current field is a target field
    const sourceField = Object.keys(FIELD_RELATIONSHIPS).find(
      (key) => FIELD_RELATIONSHIPS[key].targetField === fieldName,
    );

    if (sourceField) {
      const sourceValue = thresholdState[sourceField];
      const { compareKey } = FIELD_RELATIONSHIPS[sourceField];

      if (
        sourceValue !== null &&
        sourceValue !== undefined &&
        sourceValue > 0
      ) {
        if (compareKey === COMPARE_KEYS.GREATER && numValue <= sourceValue) {
          return {
            compareKey: COMPARE_KEYS.GREATER,
            fieldName,
            sourceField,
          };
        } else if (
          compareKey === COMPARE_KEYS.LESS &&
          numValue >= sourceValue
        ) {
          return {
            compareKey: COMPARE_KEYS.LESS,
            fieldName,
            sourceField,
          };
        }
      }
    }
    return null;
  };

  const getRelativeCompareErrorMessage = (fieldId) => {
    const error = errors[fieldId];
    if (error?.compareKey) {
      const { fieldName, sourceField, compareKey } = error;
      const compareText =
        compareKey === COMPARE_KEYS.GREATER
          ? "ValueStrings.mustBeGreaterThan"
          : "ValueStrings.mustBeLessThan";
      return `${t(`replacementThresholdStrings.${fieldName}`)} ${t(
        compareText,
      )} ${t(`replacementThresholdStrings.${sourceField}`)}`;
    }
    return null;
  };

  const handleInputChange = (field, event, ranges) => {
    const value = event.target.value;
    const { min, max } = ranges.hard;
    const numValue = value === "" ? "" : parseFloat(value);

    const newThresholdState = { ...thresholdState, [field]: numValue };
    const newFormattedFields = formatRelatedFields(field, value);
    let updatedErrors = { ...errors };

    if (Object.keys(newFormattedFields || {}).length > 0) {
      Object.assign(newThresholdState, newFormattedFields);
      const targetField = Object.keys(newFormattedFields)[0];
      updatedErrors = { ...updatedErrors, [targetField]: null };
    }

    const isEmpty = value === "" || value === null || value === undefined;
    const isInvalid = isEmpty || numValue < min || numValue > max;
    const crossFieldErrorMsg = !isEmpty
      ? validateCrossFieldRules(field, numValue)
      : null;

    if (isInvalid) {
      updatedErrors[field] = true;
    } else if (crossFieldErrorMsg) {
      updatedErrors[field] = crossFieldErrorMsg;
    } else {
      updatedErrors[field] = null;
    }
    setErrors(updatedErrors);
    setThresholdState(newThresholdState);
    dispatch(storeReplacementThreshold(newThresholdState));
  };

  const replacementThreshold = () => {
    const permeateFlow = thresholdState.designed_permeate_flow;
    if (noError && permeateFlow) {
      const thresholdValues = {
        max_feed_pressure: thresholdState.max_feed_pressure,
        max_permeate_quality: thresholdState.max_permeate_quality,
        std_warranty_period: thresholdState.std_warranty_period,
        startup_pressure_drop: thresholdState.startup_pressure_drop,
        designed_permeate_flow: thresholdState.designed_permeate_flow,
        std_membrane_age: thresholdState.std_membrane_age,
        max_pressure_drop: thresholdState.max_pressure_drop,
        min_permeate_flow: thresholdState.min_permeate_flow,
        nwp_trigger: thresholdState.nwp_trigger,
      };
      dispatch(storeReplacementThreshold(thresholdValues));
    } else {
      props.setActiveStep(0);
    }
  };
  React.useImperativeHandle(ref, () => ({ replacementThreshold }));

  return (
    <div className="ro-card-wrapper position-relative">
      <div className="ro-card-header">
        <div className="info-section">
          <div className="teal-heading">
            {t("replacementThresholdStrings.replacementThresholds")}
          </div>
          <IconWithTooltip
            label={t("helpInformation.replacementThresholdHelp")}
          />
        </div>
      </div>
      <div className="grid-container-threshold">
        {Object.keys(thresholdFieldsConfig)
          .filter(
            (fieldId) =>
              fieldId !== "nwp_trigger" || hasEditableUsePermeabilityLicense,
          )
          .map((fieldId, index) => {
            const fieldConfig = thresholdFieldsConfig[fieldId];
            const { unitLabel, ranges, isRequired } = fieldConfig;
            return (
              <div className="grid-item-threshold " key={index}>
                <WPRangedInput
                  name={fieldId}
                  key={fieldId}
                  label={t(`replacementThresholdStrings.${fieldId}`)}
                  value={thresholdState[fieldId] ?? ""}
                  onChange={(event) =>
                    handleInputChange(fieldId, event, ranges)
                  }
                  suffix={unitLabel}
                  ranges={ranges}
                  required={isRequired}
                  disabled={
                    fieldId === "nwp_trigger" &&
                    shouldDisablePermeabilityThreshold
                  }
                  requiredMsg={
                    isRequired ? t("ValueStrings.requiredField") : null
                  }
                  externalError={getRelativeCompareErrorMessage(fieldId)}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
});

export default ReplacementThreshold;
